import Flex from '@Components/Bases/Flex';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { useGreenhouseForm } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  IEsgGreenhouseMasterDataFormValues,
  ISubcommitteesFormValues,
} from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider/types';
import { useEffect } from 'react';
import { SubcommitteeType } from 'src/Enum/EsgGreenhouse';
import RolesAndResponsibilities from './RolesAndResponsibilities';
import SubcommiteeTypeList from './SubCommiteeTypeList';
import SubcommitteeChartersEn from './SubcommitteeChartersEn';
import SubcommitteeChartersTh from './SubcommitteeChartersTh';
import SubcommitteesCheckboxList from './SubcommitteesCheckboxList';
import {
  hasOtherSubcommitteeRoles,
  mapSubcommitteeFormValues,
  mapSubcommitteeFormValuesToPayload,
} from './utils';
import { modalAddSubCommitteesValidationSchema } from './validator';

type ModalSubcommitteesProps = {
  open: boolean;
  close: () => void;
  mode: 'create' | 'edit';
  rowId?: number;
  editData?: ISubcommitteesFormValues;
};
const defaultFormValues = {
  nameTh: '',
  nameEn: '',
  charterUrlTh: '',
  charterUrlEn: '',
  charterPageTh: '',
  charterPageEn: '',
  responsibilitiesTh: '',
  responsibilitiesEn: '',
  subcommitteeType: '',
  subcommitteeRoles: [],
  otherSubcommitteeRoles: [] as Array<IEsgGreenhouseMasterDataFormValues>,
};

const ModalSubcommittees = ({
  mode,
  open,
  close,
  rowId,
  editData,
}: ModalSubcommitteesProps) => {
  const initialValue =
    mode === 'edit' && editData
      ? mapSubcommitteeFormValues(editData)
      : defaultFormValues;

  const modalSubcommitteesContext = useForm<ISubcommitteesFormValues>({
    mode: 'onChange',
    resolver: zodResolver(modalAddSubCommitteesValidationSchema),
    defaultValues: initialValue,
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState,
    getValues,
    trigger: triggerModal,
  } = modalSubcommitteesContext;

  const { setValue, control, trigger } = useGreenhouseForm();

  const subcommitteeDetails =
    useWatch({
      name: 'climateChangeStructure.subcommitteeResponsibility.subcommittees',
      control,
    }) ?? [];

  const handleClose = () => {
    reset();
    close();
  };

  const watchedValues = watch([
    'responsibilitiesTh',
    'responsibilitiesEn',
    'subcommitteeType',
    'nameTh',
    'nameEn',
    'subcommitteeRoles',
    'otherSubcommitteeRoles',
  ]);

  const [
    responsibilitiesTh,
    responsibilitiesEn,
    subcommitteeType,
    nameTh,
    nameEn,
    subcommitteeRoles = [],
    otherSubcommitteeRoles = [],
  ] = watchedValues;

  useEffect(() => {
    if (open && mode === 'edit') {
      if (responsibilitiesTh === null) {
        modalSubcommitteesContext.setValue('responsibilitiesTh', '');
      }

      if (responsibilitiesEn === null) {
        modalSubcommitteesContext.setValue('responsibilitiesEn', '');
      }

      triggerModal('responsibilitiesTh');
      triggerModal('responsibilitiesEn');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, triggerModal]);

  const hasSubcommitteeRoles = subcommitteeRoles.length > 0;
  const hasOther = subcommitteeRoles.some(({ nameEn }) => nameEn === 'Other');

  const isOtherSubcommitteeRolesValid = otherSubcommitteeRoles.every(
    ({ nameTh, nameEn }) =>
      (!hasOther && !nameTh && !nameEn) || (hasOther && nameTh && nameEn)
  );

  const isSubcommitteeTypeValid =
    subcommitteeType !== SubcommitteeType.OTHER_SUBCOMMITTEE ||
    (nameTh !== '' && nameEn !== '');

  const hasErrors = Object.keys(formState.errors).length > 0;

  const isFormValid =
    hasSubcommitteeRoles &&
    responsibilitiesTh &&
    responsibilitiesEn &&
    subcommitteeType &&
    isOtherSubcommitteeRolesValid &&
    isSubcommitteeTypeValid &&
    !hasErrors;

  const isSubmitDisabled = hasOther
    ? formState.isValid && isFormValid
    : hasSubcommitteeRoles && isFormValid;

  const handleSave = () => {
    const formData = getValues();

    const currentData = mapSubcommitteeFormValuesToPayload(formData);

    const updatedSubcommittees = [...subcommitteeDetails];

    if (!hasOtherSubcommitteeRoles(subcommitteeRoles)) {
      formData.otherSubcommitteeRoles = [];
    }

    if (mode === 'create') {
      updatedSubcommittees.push(currentData);
    }

    if (mode === 'edit' && rowId !== undefined && rowId !== null) {
      updatedSubcommittees[rowId] = currentData;
    }

    setValue(
      'climateChangeStructure.subcommitteeResponsibility.subcommittees',
      updatedSubcommittees
    );
    trigger('climateChangeStructure.subcommitteeResponsibility.subcommittees');
    handleClose();
  };

  return (
    <Dialog open={open} maxWidth="md">
      <FormProvider {...modalSubcommitteesContext}>
        <form>
          <DialogTitle fontWeight={800} fontSize={16}>
            {mode === 'create' ? 'เพิ่ม' : 'แก้ไข'}
            ข้อมูลบทบาทหน้าที่ของคณะกรรมการชุดย่อย
          </DialogTitle>
          <DialogContent>
            <Typography fontWeight={800}>
              ข้อมูลบทบาทหน้าที่ของคณะกรรมการชุดย่อย
            </Typography>
            <SubcommiteeTypeList control={control} mode={mode} />
          </DialogContent>
          <DialogContent sx={{ padding: 0 }}>
            <Flex gap={2} flexDirection={'column'} pb={4} flex={1} px={3}>
              <SubcommitteesCheckboxList
                name="subcommitteeRoles"
                subtitle="บทบาทหน้าที่ของคณะกรรมการชุดย่อย"
                selectTitle="เพิ่มบทบาทหน้าที่"
                otherTitle="รายการอื่น ๆ"
                textAlign="right"
                mode={mode}
                required
                otherName="otherSubcommitteeRoles"
                control={control}
              />
              <RolesAndResponsibilities control={control} />
              <SubcommitteeChartersTh control={control} />
              <SubcommitteeChartersEn control={control} />
            </Flex>
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button color="inherit" onClick={handleClose}>
              ยกเลิก
            </Button>
            <Button
              onClick={handleSubmit(handleSave)}
              disabled={!isSubmitDisabled}
            >
              ยืนยัน
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default ModalSubcommittees;
